import { FormattedText } from '@/components/shared/components/FormattedText';

import { Condition, ConditionFile, ConditionNote } from '../ApplicantConditions/ApplicantConditions.types';
import {
    formatDateShort,
    formatDateTime,
    getFileTypeLabel,
    getProductScopeLabel,
} from '../ApplicantConditions/ApplicantConditions.utils';
import { getReviewTeams, REVIEW_TEAM_LABEL } from '../ApplicantConditions/conditionTemplates';

interface PrintApplicantConditionsProps {
    openConditions: Condition[];
    closedConditions: Condition[];
    deletedConditions: Condition[];
}

type PrintTimelineItem =
    | { type: 'note'; date: string; note: ConditionNote }
    | { type: 'file'; date: string; file: ConditionFile };

const buildTimeline = (condition: Condition): PrintTimelineItem[] =>
    [
        ...condition.notes.map((note) => ({ type: 'note' as const, date: note.dateCreated, note })),
        ...condition.files.map((file) => ({ type: 'file' as const, date: file.uploadDate, file })),
    ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

const printTeamLabels = (condition: Condition) => {
    const teams = getReviewTeams(condition.reviewTeams);
    return teams.length > 0 ? ` · ${teams.map((team) => REVIEW_TEAM_LABEL[team]).join(', ')}` : '';
};

const PrintConditionCard = ({
    condition,
    isDeleted,
}: {
    condition: Condition;
    isDeleted?: boolean;
}) => {
    const scope = getProductScopeLabel(condition.products);
    const blocksApprovalScope = getProductScopeLabel(condition.blocksApproval);
    const timeline = buildTimeline(condition);

    return (
        <div className="print-condition-card">
            <div className="print-condition-description">
                <FormattedText>{condition.description}</FormattedText>
            </div>
            <div className="print-condition-meta">
                Created by {condition.createdBy} · {formatDateShort(condition.dateCreated)}
                {scope ? ` · ${scope}` : ''}
                {printTeamLabels(condition)}
                {blocksApprovalScope ? ` · Blocks approval for ${blocksApprovalScope}` : ''}
            </div>
            {isDeleted && condition.modifiedBy && condition.dateDeleted ? (
                <div className="print-condition-status print-condition-status-deleted">
                    Deleted by {condition.modifiedBy} · {formatDateTime(condition.dateDeleted)}
                </div>
            ) : condition.resolved && condition.resolvedBy && condition.dateResolved ? (
                <div className="print-condition-status print-condition-status-resolved">
                    Resolved by {condition.resolvedBy} · {formatDateTime(condition.dateResolved)}
                </div>
            ) : null}
            {timeline.length > 0 ? (
                <ul className="print-condition-timeline">
                    {timeline.map((item) => {
                        if (item.type === 'note') {
                            const bodyClassName = [
                                'print-condition-note-body',
                                item.note.isResolutionNote
                                    ? 'print-condition-note-body-resolution'
                                    : '',
                            ]
                                .filter(Boolean)
                                .join(' ');
                            return (
                                <li key={`note-${item.note.id}`} className="print-condition-note">
                                    <div className="print-condition-note-header">
                                        {item.note.createdBy} ·{' '}
                                        {formatDateTime(item.note.dateCreated)}
                                        {item.note.isResolutionNote ? ' — Resolution note' : ''}
                                    </div>
                                    <div className={bodyClassName}>
                                        <FormattedText>{item.note.note}</FormattedText>
                                    </div>
                                </li>
                            );
                        }
                        const typeLabel = getFileTypeLabel(item.file.type);
                        return (
                            <li key={`file-${item.file.id}`} className="print-condition-file">
                                📎 {item.file.filename}
                                {typeLabel && typeLabel !== 'Other' ? ` (${typeLabel})` : ''}
                            </li>
                        );
                    })}
                </ul>
            ) : (
                <div className="print-condition-no-notes">No notes yet.</div>
            )}
        </div>
    );
};

const PrintConditionsSection = ({
    title,
    conditions,
    isDeleted,
}: {
    title: string;
    conditions: Condition[];
    isDeleted?: boolean;
}) => (
    <div className="print-conditions-subsection">
        <div className="print-conditions-subheading">
            {title} ({conditions.length})
        </div>
        {conditions.length === 0 ? (
            <div className="print-condition-no-notes">None.</div>
        ) : (
            conditions.map((condition) => (
                <PrintConditionCard key={condition.id} condition={condition} isDeleted={isDeleted} />
            ))
        )}
    </div>
);

export const PrintApplicantConditions = ({
    openConditions,
    closedConditions,
    deletedConditions,
}: PrintApplicantConditionsProps) => (
    <div className="print-conditions">
        <PrintConditionsSection title="Open Tasks/Conditions" conditions={openConditions} />
        <PrintConditionsSection title="Closed Tasks/Conditions" conditions={closedConditions} />
        {deletedConditions.length > 0 ? (
            <PrintConditionsSection
                title="Deleted Tasks/Conditions"
                conditions={deletedConditions}
                isDeleted
            />
        ) : null}
    </div>
);
